import type { User } from '@gokrew/models/auth';
import { getNameFromObj } from '@/utility/utility';
import { getConfig } from '@/runtime-config';
import Userback, { type UserbackWidget } from '@userback/widget';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

interface UserbackContextType {
  userback: UserbackWidget | null;
  setUserInfo: (userbackOption: User) => void;
  resetUserInfo: () => void;
}

const UserbackContext = createContext<UserbackContextType | null>(null);

export function UserbackProvider({ children }: { children: React.ReactNode }) {
  const [userback, setUserback] = useState<UserbackWidget | null>(null);

  const setUserInfo = useCallback(
    ({ id, email, ...user }: User) => {
      if (!userback) return;
      userback.identify(id, { email, name: getNameFromObj(user) });
    },
    [userback]
  );

  const resetUserInfo = useCallback(() => {
    if (!userback) return;
    userback.setName('');
    userback.setEmail('');
  }, [userback]);

  const initUserback = async () => {
    try {
      const userbackToken = getConfig()?.USERBACK_TOKEN;
      if (!userbackToken) return;
      const instance = await Userback(userbackToken, {
        user_data: {},
        // Keep the launcher hidden until the widget has finished loading and
        // applying its own styles, then reveal it. Without this the button node
        // is painted before Userback's remote CSS arrives, briefly rendering as a
        // full-width unstyled bar (FOUC) — most visible on slow connections.
        autohide: true,
        on_load: () => {
          document.body.classList.add('userback-loaded');
          instance.show();
        },
      });
      setUserback(instance);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to initialize Userback widget:', error);
    }
  };
  useEffect(() => {
    initUserback();
  }, []);

  return (
    <UserbackContext.Provider value={{ userback, setUserInfo, resetUserInfo }}>
      {children}
    </UserbackContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUserback(): UserbackContextType {
  const context = useContext(UserbackContext);
  if (!context)
    throw new Error('useUserback must be used inside UserbackProvider');
  return context;
}
